import { Light, PointLight, SpotLight } from '@babylonjs/core/Lights';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { LightNode } from '@models/classes/lights/light-node';
import { KbViewer } from '@view/kb-viewer';
import { createLightVisualization } from '../../helpers/light-helper';
import { KbGizmo } from './gizmo';
import { KbPositionGizmo } from './position-gizmo';
import { KbRotationGizmo } from './rotation-gizmo';

/**
 * Gizmo that enables dragging and rotating lights
 */
export class KbLightGizmo extends KbGizmo {
    /**
     * Internal gizmo used for positioning lights
     */
    posGizmo: KbPositionGizmo | undefined;
    /**
     * Internal gizmo used for rotating lights
     */
    rotGizmo: KbRotationGizmo | undefined;

    private _attachedLight?: LightNode;
    private _helperMesh: AbstractMesh | undefined;

    lightPositionGizmo: Mesh | undefined;

    attachTo(lightNode: LightNode) {
        const light = this.kbViewer.getRendererNode(lightNode);
        if (!light) {
            console.warn('could not find light in scene');
            return;
        }
        this._attachedLight = lightNode;

        if (this.posGizmo) {
            this.posGizmo.dispose();
        }
        if (this.rotGizmo) {
            this.rotGizmo.dispose();
        }
        const _helperMesh = (this._helperMesh = new AbstractMesh('', this.gizmoLayer.utilityLayerScene));
        _helperMesh.rotationQuaternion = Quaternion.Identity();
        const lightVisual = this.updatePreviewMesh(light);
        _helperMesh.addChild(lightVisual);
    }

    /**
     * Creates a PositionGizmo
     * @param gizmoLayer The utility layer the gizmo will be added to
     */
    constructor(
        private kbViewer: KbViewer,
        gizmoLayer: UtilityLayerRenderer = UtilityLayerRenderer.DefaultUtilityLayer
    ) {
        super(gizmoLayer);

        this.attachedMesh = null;
    }

    /**
     * Disposes of the gizmo
     */
    dispose() {
        if (this.posGizmo) {
            this.posGizmo.dispose();
        }
        if (this.rotGizmo) {
            this.rotGizmo.dispose();
        }
        if (this._helperMesh) {
            this._helperMesh.dispose();
        }
        if (this.lightPositionGizmo) {
            this.lightPositionGizmo.dispose();
        }
        delete this._attachedLight;
    }

    updateGizmoPosition(lightNode?: LightNode) {
        if (!lightNode) {
            return;
        }
        const light = this.kbViewer.getRendererNode(lightNode);
        if (light) {
            if (!this._attachedLight || lightNode._dynamicId !== this._attachedLight._dynamicId) {
                this.attachTo(lightNode);
            } else {
                this.updatePreviewMesh(light);
            }
        }
    }

    protected updatePreviewMesh(light: Light) {
        const scene = this.gizmoLayer.utilityLayerScene;

        if (!this.lightPositionGizmo) {
            this.lightPositionGizmo = createLightVisualization(light, scene);
        }

        if (light instanceof SpotLight || light instanceof PointLight) {
            this.lightPositionGizmo.position = light.parent
                ? Vector3.TransformCoordinates(light.position, light.parent.computeWorldMatrix(true).clone().invert())
                : light.position;
        }

        return this.lightPositionGizmo;
    }
}
